// @ts-nocheck
// /c/<token> — "סדנה בשיחה" (22.9.2026). A buyer opens her personal link
// (no login) and does session 1 of the workshop as a conversation: the bot
// asks, she answers in two lines, the bot replies personally (Claude, via
// the chat-workshop edge function) and asks the next scripted question.
// Shlomo owns the questions (chat_questions table); Claude only writes the
// short reply. The link is the identity; nothing is stored in the browser.

import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";

const styles = `
.cw-wrap { direction: rtl; font-family: 'Heebo', sans-serif; max-width: 560px; margin: 0 auto; padding: 12px 12px 120px; min-height: 100vh; background: #F6F1E8; }
.cw-head { display: flex; align-items: center; gap: 12px; padding: 10px 6px 14px; border-bottom: 1px solid #E3D7C3; margin-bottom: 14px; }
.cw-avatar { width: 46px; height: 46px; border-radius: 50%; background: #5C3D2E; color: #F0C27B; display: flex; align-items: center; justify-content: center; font-family: 'Frank Ruhl Libre', serif; font-size: 22px; font-weight: 700; }
.cw-title { font-family: 'Frank Ruhl Libre', serif; font-size: 20px; font-weight: 700; color: #2D1B10; line-height: 1.2; }
.cw-sub { font-size: 12.5px; color: #8B7355; }
.cw-msgs { display: flex; flex-direction: column; gap: 10px; }
.cw-m { max-width: 86%; padding: 12px 15px; border-radius: 18px; font-size: 16px; line-height: 1.65; white-space: pre-wrap; word-break: break-word; }
.cw-m.bot { align-self: flex-start; background: #fff; color: #2D1B10; border-bottom-right-radius: 6px; box-shadow: 0 1px 2px rgba(92,61,46,.08); }
.cw-m.user { align-self: flex-end; background: #DDF2E4; color: #1F3A2A; border-bottom-left-radius: 6px; }
.cw-m.q { border-right: 4px solid #A0704A; font-weight: 600; }
.cw-typing { align-self: flex-start; color: #8B7355; font-size: 14px; padding: 6px 12px; }
.cw-bar { position: fixed; bottom: 0; left: 0; right: 0; background: #F6F1E8; border-top: 1px solid #E3D7C3; padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px)); }
.cw-bar-in { max-width: 560px; margin: 0 auto; display: flex; gap: 8px; align-items: flex-end; }
.cw-ta { flex: 1; border: 1.5px solid #D9C8B0; border-radius: 16px; padding: 12px 14px; font-family: 'Heebo', sans-serif; font-size: 16px; line-height: 1.5; resize: none; min-height: 48px; max-height: 160px; background: #fff; direction: rtl; }
.cw-ta:focus { outline: 2px solid rgba(160,112,74,.45); }
.cw-send { background: linear-gradient(135deg, #A0704A, #D4875A); color: #fff; border: none; border-radius: 16px; padding: 12px 20px; font-family: 'Heebo', sans-serif; font-size: 16px; font-weight: 700; cursor: pointer; min-height: 48px; }
.cw-send:disabled { opacity: .5; cursor: default; }
.cw-done { text-align: center; color: #5C3D2E; font-size: 15px; padding: 14px; }
.cw-done a { color: #A0704A; font-weight: 700; }
.cw-err { text-align: center; color: #C62828; font-size: 14px; padding: 8px; }
.cw-404 { text-align: center; padding: 60px 20px; color: #5C3D2E; font-family: 'Heebo', sans-serif; direction: rtl; }
`;

const FN = "chat-workshop";

async function call(method: "GET" | "POST", payload: Record<string, unknown>) {
  const base = process.env.REACT_APP_SUPABASE_URL;
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const url = method === "GET" ? `${base}/functions/v1/${FN}?token=${encodeURIComponent(String(payload.token))}` : `${base}/functions/v1/${FN}`;
  const r = await fetch(url, {
    method,
    headers: { "content-type": "application/json", apikey: key, authorization: `Bearer ${key}` },
    body: method === "POST" ? JSON.stringify(payload) : undefined,
  });
  if (!r.ok) throw new Error(String(r.status));
  return r.json();
}

export function ChatWorkshopPage({ token }: { token: string }) {
  const [state, setState] = useState<"loading" | "ok" | "missing" | "error">("loading");
  const [data, setData] = useState<{ first_name: string | null; status: string; awaiting?: boolean; messages: { role: string; content: string; at: string }[] } | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      try {
        let d = await call("GET", { token });
        if (d.messages.length === 0 && d.status === "open") d = await call("POST", { token, action: "start" });
        setData(d);
        setState("ok");
      } catch (e) {
        setState(String(e).includes("404") ? "missing" : "error");
      }
    })();
  }, [token]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [data?.messages.length, busy]);

  const send = async () => {
    const clean = text.trim();
    if (!clean || busy) return;
    setBusy(true);
    setErr("");
    setData((d) => d && { ...d, messages: [...d.messages, { role: "user", content: clean, at: new Date().toISOString() }] });
    setText("");
    try {
      const d = await call("POST", { token, message: clean });
      setData(d);
    } catch {
      setErr("לא הצלחנו לשלוח. בדקי את החיבור ונסי שוב.");
      setText(clean);
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading") return <div className="cw-404">רגע...</div>;
  if (state === "missing") return <div className="cw-404"><h2>הקישור הזה לא מוכר</h2><p>אולי חסר בו תו. אפשר לכתוב לשלמה בוואטסאפ 054-4562084 ונשלח חדש.</p></div>;
  if (state === "error" || !data) return <div className="cw-404"><h2>משהו השתבש</h2><p>נסי לרענן את הדף. אם זה חוזר, כתבי לשלמה בוואטסאפ 054-4562084.</p></div>;

  const open = data.status === "open" && !data.awaiting;
  const questionSet = new Set<number>();
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="cw-wrap">
        <div className="cw-head">
          <div className="cw-avatar">ש</div>
          <div>
            <div className="cw-title">שאלת האריה · סדנה בשיחה</div>
            <div className="cw-sub">{data.first_name ? `${data.first_name}, ` : ""}שאלה אחת בכל פעם. עונים בשתי שורות, בזמן שנוח לך.</div>
          </div>
        </div>
        <div className="cw-msgs">
          {data.messages.map((m, i) => {
            const isQ = m.role === "bot" && /\?\s*$/.test(m.content) && !m.content.includes("\n");
            return <div key={i} className={"cw-m " + m.role + (isQ ? " q" : "")}>{m.content}</div>;
          })}
          {err && <div className="cw-err">{err}</div>}
          <div ref={endRef} />
        </div>
        {open && (
          <div className="cw-bar">
            <div className="cw-bar-in">
              <textarea
                id="cw-input"
                className="cw-ta"
                rows={1}
                placeholder="כתבי כאן..."
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                disabled={busy}
              />
              <button className="cw-send" onClick={send} disabled={busy || !text.trim()}>שליחה</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
